import { faInfoCircle as solidInfoCircle } from "@jewlr/pro-solid-svg-icons/faInfoCircle"
import loadable from "@loadable/component"
import { AnimatePresence } from "framer-motion"
import PropTypes from "prop-types"
import { Suspense, useEffect, useState } from "react"
import { connect } from "react-redux"

import { Footnote, FootnoteText } from "areas/product/jewlr/components"
import { updateChoice } from "areas/product/store/actions"
import { SlideContainer, MainContainer } from "components/HalfModal/slides"
import Modal from "components/modal"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { retry, jewlr_t } from "helpers/application"
import StorageHelper from "helpers/local-storage-cache"
import useWindowDimensions from "helpers/window-dimensions"

const ImageCropper = loadable(() =>
  retry(() => import("areas/product/jewlr/image-cropper"))
)
const PhotoUpload = loadable(() =>
  retry(() => import("areas/product/jewlr/photo-upload"))
)
const PhotoTips = loadable(() => retry(() => import("./photo_tips")), {
  ssr: false,
})

const Index = {
  Upload: 0,
  // eslint-disable-next-line sort-keys
  Edit: 1,
  Tips: 2,
  TipsWithImgData: 3,
}

const InternalPhotoUploadModal = ({ pref, wizardData, routerHash }) => {
  const [contentIndex, setIndex] = useState(Index.Upload)
  const [cropperData, setCropperData] = useState({})
  const handwritingEngraving = pref && wizardData[pref]?.handwriting_engraving
  const photoLocket = pref && wizardData[pref]?.photo_locket
  const { isMobile } = useWindowDimensions()
  const isTipsIndex =
    contentIndex === Index.Tips || contentIndex == Index.TipsWithImgData

  useEffect(() => {
    let showTips = false
    if (routerHash.includes("#pendant-upload")) {
      const routerHashStack = routerHash.split("-")
      if (routerHash.includes("tips")) {
        showTips = true
        routerHashStack.pop()
      }
    }
    const cropper = wizardData && wizardData[pref] && wizardData[pref].cropper
    const key = `image-${pref}-${cropper?.aspect_ratio_width}-${cropper?.aspect_ratio_height}`
    if (wizardData && wizardData[pref] && !wizardData[pref].disabled) {
      if (StorageHelper.exist(key.trim())) {
        setIndex(showTips ? Index.TipsWithImgData : Index.Edit)
      } else {
        setIndex(showTips ? Index.Tips : Index.Upload)
      }
    }
    return () => {
      if (contentIndex == Index.Tips || contentIndex == Index.TipsWithImgData) {
        if (StorageHelper.exist(key.trim())) {
          setIndex(Index.Edit)
        } else {
          setIndex(Index.Upload)
        }
      }
    }
  }, [pref, routerHash])

  const content = (
    <AnimatePresence>
      <Suspense fallback={<div></div>} key="photo-tips-suspense">
        {isTipsIndex && (
          <SlideContainer key="photo-tips">
            <PhotoTips pref={pref} />
          </SlideContainer>
        )}
      </Suspense>
      {contentIndex == Index.Edit && (
        <MainContainer animate="show" key="crop">
          <div className="tablet:[&>div]:flex-row -mt-1 flex w-full justify-center text-center [&>div]:flex [&>div]:flex-col">
            <ImageCropper
              cropperData={cropperData[pref]}
              onTipClick={() => setIndex(Index.TipsWithImgData)}
              pref={pref}
              setCropperData={(data) => {
                setCropperData({
                  ...cropperData,
                  [pref]: data,
                })
              }}
            />
          </div>
        </MainContainer>
      )}
      {contentIndex == Index.Upload && (
        <MainContainer
          animate={contentIndex != Index.Upload ? "hidden" : "show"}
          key="upload"
        >
          <div className="-mt-1 flex w-full justify-center text-center [&>div]:flex [&>div]:flex-col">
            <div className="w-full">
              <PhotoUpload
                onUpload={() => {
                  setIndex(Index.Edit)
                  setCropperData({
                    ...cropperData,
                    [pref]: {},
                  })
                }}
                pref={pref}
              />
            </div>
          </div>
          <Footnote>
            <FootnoteText>
              Photo or image uploads that include inappropriate, explicit, or
              illegal content will not be accepted.
            </FootnoteText>
          </Footnote>
          <div className="mb-8 pt-12 text-center">
            <Button
              className="text-14 mx-auto"
              onClick={() => setIndex(Index.Tips)}
              text
              variant="secondary"
            >
              <FontAwesomeIcon className="mr-8" icon={solidInfoCircle} />
              <span>Read our tips for best results</span>
            </Button>
          </div>
        </MainContainer>
      )}
    </AnimatePresence>
  )

  let labelText = "Photo Upload"
  if (handwritingEngraving) {
    labelText = isTipsIndex ? "Handwriting Image Tips" : "Image Upload"
  } else if (photoLocket && isTipsIndex) {
    labelText = `${jewlr_t("terms.Personalized")} Photo Tips`
  }

  return (
    <Modal
      contentClassName="px-16 tablet:px-24"
      contentIndex={contentIndex}
      height={contentIndex === 0 ? "fit-content" : "100%"}
      label={labelText}
      onBack={() => {
        if (contentIndex == Index.TipsWithImgData) {
          setIndex(Index.Edit)
        } else if (contentIndex == Index.Tips) {
          setIndex(Index.Upload)
        }
      }}
      show={
        pref === "box"
          ? "#photo-upload"
          : [`#pendant-upload-${pref}`, `#pendant-upload-${pref}-tips`]
      }
      variant={!isMobile || contentIndex === 0 ? "squircle" : "base"}
      width={
        contentIndex == Index.Upload
          ? "482px"
          : contentIndex == Index.Edit
            ? "812px"
            : "612px"
      }
      withBackButton={contentIndex !== Index.Edit}
    >
      {content}
    </Modal>
  )
}

InternalPhotoUploadModal.propTypes = {
  pref: PropTypes.string,
  routerHash: PropTypes.string,
  setChoice: PropTypes.func,
  wizardData: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    routerHash: state.router.location.hash,
    wizardData: state.wizard.data,
  }
}
const mapDispatchToProps = (dispatch) => {
  return {
    setChoice: (pref, data) => {
      dispatch(updateChoice(pref, data))
    },
  }
}

const PhotoUploadModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalPhotoUploadModal)

export default PhotoUploadModal
